/**
 * whack-a-mole.js — GDD 6.6.2. Two mallets, sixty seconds.
 *
 * A 3×3 table of holes. Moles pop up for a window that shrinks from 1.5 s at
 * the start of the round to 0.4 s at the end, so the finish is frantic. A
 * standard mole is 50, a golden one 200 (and only visible for 0.8 s), and a
 * bomb costs 100 if you hit it. One 60-second timed round.
 *
 * Hits are geometric, not physics contacts: each mallet-head world position is
 * tracked per frame and tested against the moles that are currently up. Two
 * kinematic bodies (a held mallet and a raised mole) generate no Rapier contact
 * pair at all, so a contact-event approach would silently never fire. Swinging
 * at the table or an empty hole therefore does nothing at all, which is exactly
 * the GDD's "miss = nothing".
 *
 * Geometry lives in `whack-a-mole-parts.js`.
 *
 * @module stalls/whack-a-mole
 */

import * as THREE from 'three';
import { WHACK_A_MOLE, EVENTS } from '../constants.js';
import { StallBase, registerStall } from './framework.js';
import * as factory from '../assets3d/factory.js';
import * as parts from './whack-a-mole-parts.js';
import { grab } from '../core/grab.js';
import { bus } from '../core/events.js';
import { rng } from '../core/testmode.js';
import { createStallLight } from '../world/lighting.js';
import { haptics } from '../core/haptics.js';

const CFG = WHACK_A_MOLE;
/** Head radius of the one-handed mallet, from the factory's dimensions. */
const HEAD_RADIUS = 0.055;

class WhackAMole extends StallBase {
  constructor(ctx) {
    super(ctx);
    /** One entry per hole: type, state machine, and its kinematic body. */
    this.moles = [];
    /** @type {object[]} Mallet handles, one intended per hand. */
    this.mallets = [];
    this._malletRest = [];
    this._malletCooldown = [0, 0];
    this._headOffset = new THREE.Vector3(0, 0.34, 0);

    this._spawnTimer = 0;
    this._elapsed = 0;
    this._timeScale = 1;
    this.hits = { standard: 0, golden: 0, bomb: 0 };
    this.misses = 0;
  }

  // ── Static build ────────────────────────────────────────────────────────

  onBuild() {
    const floor = parts.createFloorSlab();
    this.spawn(floor, { x: 0, y: -0.05, z: 1.2 }, {
      type: 'fixed', shape: 'box', halfExtents: { x: 1.7, y: 0.05, z: 1.7 },
      position: this.localToWorld(0, -0.05, 1.2),
      friction: 0.9, restitution: 0.05,
    });

    const table = parts.createTable();
    this.spawn(table, { x: 0, y: CFG.tableY, z: CFG.tableZ }, {
      type: 'fixed', shape: 'box',
      halfExtents: { x: CFG.tableWidth / 2, y: 0.045, z: CFG.tableDepth / 2 },
      position: this.localToWorld(0, CFG.tableY, CFG.tableZ),
      friction: 0.8, restitution: 0.1,
    });

    // Hole recesses are decor only — the mole body is what gets hit.
    for (const local of parts.holeGrid()) {
      const hole = parts.createHole();
      hole.position.set(local.x, CFG.tableY - 0.07, local.z);
      this.root.add(hole);
    }

    this.buildScoreboard({ y: 2.1, z: 2.0, width: 1.4, height: 0.85 });

    const light = createStallLight(0xffe4b5, 2.1, 9);
    light.position.set(0, 2.6, 1.2);
    this.root.add(light);
  }

  // ── Round setup ─────────────────────────────────────────────────────────

  setupRound() {
    // GDD 6.6.2: one 60-second round rather than a fixed number of attempts.
    this.setRoundFormat({ seconds: CFG.roundSeconds });
    this.moles = [];
    this.mallets = [];
    this._malletRest = [];
    this._malletCooldown = [0, 0];
    this._spawnTimer = 0;
    this._elapsed = 0;
    this.hits = { standard: 0, golden: 0, bomb: 0 };
    this.misses = 0;

    parts.holeGrid().forEach((local, index) => {
      const mesh = parts.createMole('standard');
      const downY = local.y - CFG.moleSink;
      const handle = this.spawn(mesh, { x: local.x, y: downY, z: local.z }, {
        type: 'kinematic', shape: 'capsule',
        radius: CFG.moleRadius, halfHeight: CFG.moleHeight * 0.45,
        position: this.localToWorld(local.x, downY, local.z),
      });
      handle.userData.moleIndex = index;
      this.registerTarget(handle, { id: `mole-${index}`, group: 'mole' });

      this.moles.push({
        index,
        local,
        handle,
        mesh,
        type: 'standard',
        state: 'down',
        timer: 0,
        window: CFG.visibilityStart,
      });
    });

    this._spawnMallets();
  }

  /**
   * Two mallets, one per hand (GDD 6.6.2 dual wielding). Neither is a
   * throwable: releasing one costs nothing in a timed round.
   * @private
   */
  _spawnMallets() {
    for (let i = 0; i < CFG.malletCount; i++) {
      const mesh = factory.createMallet('mole');
      // Mirrored about the centre line so one lands within each hand's 0.45 m
      // grab radius: mallet 0 at −x for the RIGHT hand, mallet 1 at +x for the
      // LEFT (stall-local +X is the player's left).
      const side = i === 0 ? -1 : 1;
      const rest = {
        x: CFG.malletRest.x * side,
        y: CFG.malletRest.y,
        z: CFG.malletRest.z,
      };
      const handle = this.spawn(mesh, rest, {
        position: this.localToWorld(rest.x, rest.y, rest.z),
        linearDamping: 0.3, angularDamping: 0.5,
      });
      handle.userData.mallet = true;
      grab.register(handle, {
        kind: 'mallet',
        holdOffset: mesh.userData.holdOffset || null,
        onGrab: () => { handle.userData.parked = false; },
      });
      this.mallets.push(handle);
      this._malletRest.push(handle.position().clone());
      if (i === 0) {
        this._headOffset = (mesh.userData.headOffset || new THREE.Vector3(0, 0.34, 0)).clone();
      }
      this._park(i);
    }
  }

  /**
   * Hold a mallet still at its rest pose. A dynamic body spawned at grab height
   * topples off the table the instant the round starts, and the leash below
   * would then drop it again every frame. `grab` restores it to dynamic on
   * release, so a real swing is unaffected.
   * @param {number} i Which mallet.
   * @private
   */
  _park(i) {
    const handle = this.mallets[i];
    if (!handle || handle.removed) return;
    handle.setPosition(this._malletRest[i]);
    handle.setRotation(this.worldQuaternion());
    handle.freeze();
    handle.setKinematic(true);
    handle.userData.parked = true;
  }

  // ── Per-frame ───────────────────────────────────────────────────────────

  /**
   * Apply the test time scale before the base class ticks the round clock.
   * Overriding here rather than in `onUpdate` is what lets a test fast-forward
   * a real 60-second round instead of waiting it out under software rendering.
   * @param {number} dt
   * @internal
   */
  _update(dt) {
    super._update(dt * this._timeScale);
  }

  onUpdate(dt) {
    this._elapsed += dt;
    this._updateMoles(dt);
    this._updateSpawning(dt);
    this._checkMalletHits(dt);
    this._leashMallets();
  }

  /**
   * How far through the round we are, 0 at the start and 1 at the end. Drives
   * both the visibility window and the spawn cadence (GDD 6.6.2).
   * @returns {number} @private
   */
  _progress() {
    if (!CFG.roundSeconds) return 0;
    return THREE.MathUtils.clamp(1 - this.timeLeft / CFG.roundSeconds, 0, 1);
  }

  /** @private */
  _updateMoles(dt) {
    for (const mole of this.moles) {
      mole.timer += dt;

      if (mole.state === 'rising') {
        const k = Math.min(1, mole.timer / CFG.popTime);
        this._setMoleHeight(mole, this._lerpHeight(mole, k));
        if (k >= 1) { mole.state = 'up'; mole.timer = 0; }
      } else if (mole.state === 'up') {
        if (mole.timer >= mole.window) this._retract(mole, false);
      } else if (mole.state === 'falling') {
        const k = Math.min(1, mole.timer / CFG.popTime);
        this._setMoleHeight(mole, this._lerpHeight(mole, 1 - k));
        if (k >= 1) { mole.state = 'down'; mole.timer = 0; }
      } else if (mole.state === 'hit') {
        // Squash, then drop back into the hole.
        const k = Math.min(1, mole.timer / CFG.squashTime);
        mole.mesh.scale.set(1 + k * 0.35, 1 - k * 0.7, 1 + k * 0.35);
        this._setMoleHeight(mole, this._lerpHeight(mole, 1 - k));
        if (k >= 1) {
          mole.mesh.scale.set(1, 1, 1);
          mole.state = 'down';
          mole.timer = 0;
        }
      }
    }
  }

  /**
   * @param {object} mole @param {number} k 0 = fully down, 1 = fully up.
   * @returns {number} Local Y for the mole's centre. @private
   */
  _lerpHeight(mole, k) {
    const down = mole.local.y - CFG.moleSink;
    const up = mole.local.y + CFG.moleRise;
    return down + (up - down) * THREE.MathUtils.clamp(k, 0, 1);
  }

  /** @private */
  _setMoleHeight(mole, y) {
    if (mole.handle.removed) return;
    mole.handle.setPosition(this.localToWorld(mole.local.x, y, mole.local.z));
  }

  /** @private */
  _retract(mole, wasHit) {
    mole.state = wasHit ? 'hit' : 'falling';
    mole.timer = 0;
    if (!wasHit && mole.type !== 'bomb') this.misses += 1;
  }

  // ── Spawning ────────────────────────────────────────────────────────────

  /** @private */
  _updateSpawning(dt) {
    if (!this.roundActive) return;
    this._spawnTimer -= dt;
    if (this._spawnTimer > 0) return;

    const p = this._progress();
    this._spawnTimer = THREE.MathUtils.lerp(
      CFG.spawnIntervalStart, CFG.spawnIntervalEnd, p,
    );

    const active = this.moles.filter((m) => m.state !== 'down').length;
    if (active >= CFG.maxConcurrent) return;

    const idle = this.moles.filter((m) => m.state === 'down');
    if (idle.length === 0) return;

    // Every gameplay random number goes through the seeded generator, or stall
    // tests go flaky (ARCHITECTURE §11).
    const mole = idle[Math.floor(rng() * idle.length)];
    this._pop(mole, this._rollType(), null);
  }

  /** @returns {'standard'|'golden'|'bomb'} @private */
  _rollType() {
    const roll = rng();
    if (roll < CFG.goldenChance) return 'golden';
    if (roll < CFG.goldenChance + CFG.bombChance) return 'bomb';
    return 'standard';
  }

  /**
   * Raise a mole.
   * @param {object} mole @param {string} type
   * @param {number|null} seconds Override the visibility window. @private
   */
  _pop(mole, type, seconds) {
    mole.type = type;
    mole.mesh.userData.setType(type);
    mole.mesh.scale.set(1, 1, 1);
    mole.handle.userData.moleType = type;
    mole.state = 'rising';
    mole.timer = 0;
    mole.window = seconds ?? (
      type === 'golden'
        ? CFG.goldenVisibility
        : THREE.MathUtils.lerp(CFG.visibilityStart, CFG.visibilityEnd, this._progress())
    );
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'mole-pop', volume: 0.5 });
  }

  // ── Hits ────────────────────────────────────────────────────────────────

  /** @private */
  _checkMalletHits(dt) {
    for (let i = 0; i < this.mallets.length; i++) {
      this._malletCooldown[i] = Math.max(0, this._malletCooldown[i] - dt);
      if (this._malletCooldown[i] > 0) continue;

      const handle = this.mallets[i];
      if (!handle || handle.removed || !handle.mesh) continue;
      handle.mesh.updateWorldMatrix(true, false);
      const headWorld = new THREE.Vector3();
      headWorld.copy(this._headOffset).applyMatrix4(handle.mesh.matrixWorld);

      const mole = this._moleUnderPoint(headWorld);
      if (!mole) continue;
      // Mallet 0 sits at negative local X. Stall-local +X is the player's LEFT,
      // so negative X is the RIGHT hand — the haptic has to fire on that side.
      this._hitMole(mole, i === 0 ? 'right' : 'left');
      this._malletCooldown[i] = CFG.hitCooldown;
    }
  }

  /**
   * The raised mole a point is touching, if any.
   * @param {THREE.Vector3} worldPoint @returns {object|null}
   * @private
   */
  _moleUnderPoint(worldPoint) {
    const reach = CFG.moleRadius + HEAD_RADIUS;
    for (const mole of this.moles) {
      if (mole.state !== 'up' && mole.state !== 'rising') continue;
      if (mole.handle.removed) continue;
      if (mole.handle.position().distanceTo(worldPoint) <= reach) return mole;
    }
    return null;
  }

  /**
   * Score a bonk. Also the path the `whack` test hook drives.
   * @param {object} mole @param {'left'|'right'} side
   * @returns {{ index: number, type: string, points: number }}
   * @private
   */
  _hitMole(mole, side = 'right') {
    const points = CFG.points[mole.type] ?? 0;
    const worldPos = mole.handle.position();

    this.hits[mole.type] = (this.hits[mole.type] || 0) + 1;
    mole.handle.userData.scored = true;
    this._retract(mole, true);

    this.addScore(points, {
      position: worldPos,
      label: `${mole.type} mole`,
      color: mole.type === 'bomb' ? '#ff8f8f' : (mole.type === 'golden' ? '#ffd700' : '#ffd166'),
    });

    bus.emit(EVENTS.AUDIO_PLAY, {
      name: mole.type === 'bomb' ? 'mole-bomb' : 'mole-bonk',
      volume: mole.type === 'golden' ? 1 : 0.8,
    });
    haptics.play(side, 'impact', { force: mole.type === 'bomb' ? 30 : 18 });

    return { index: mole.index, type: mole.type, points };
  }

  /**
   * A dropped mallet cannot be recovered on desktop, where the hand cannot
   * reach the floor, so put it back within reach. @private
   */
  _leashMallets() {
    for (let i = 0; i < this.mallets.length; i++) {
      const handle = this.mallets[i];
      if (!handle || handle.removed) continue;
      if (handle.userData.parked) continue;
      if (grab.heldBy('left') === handle || grab.heldBy('right') === handle) continue;
      const pos = handle.position();
      const strayed = pos.distanceTo(this._malletRest[i]) > CFG.malletLeashRadius;
      const onFloor = this.worldToLocal(pos).y < 0.3;
      if (!strayed && !onFloor) continue;
      if (handle.velocity().lengthSq() > 0.25) continue; // still in flight
      this._park(i);
    }
  }

  // ── Scoreboard ──────────────────────────────────────────────────────────

  updateScoreboard() {
    if (!this.scoreboard) return;
    this.scoreboard.setLines([
      `Score   ${this.score}`,
      `Time    ${Math.ceil(Math.max(0, this.timeLeft))}s`,
      `Moles   ${this.hits.standard}   Gold ${this.hits.golden}`,
      `Bombs   ${this.hits.bomb}`,
    ]);
  }

  // ── Test automation ─────────────────────────────────────────────────────

  /**
   * Additive hooks reached through `__cc.stalls.action(name, args)`, plus the
   * `pokeMole` / `whackMole` wrappers on `__cc.stalls`.
   *
   * `pop` and `whack` drive `_pop` and `_hitMole` — the same methods the
   * scheduler and the mallet-head detection call — so a test asserts the real
   * scoring path rather than a parallel one.
   *
   * @param {string} name
   * @param {object} args
   */
  testHook(name, args = {}) {
    switch (name) {
      case 'pop': {
        const mole = this.moles[args.index ?? 0];
        if (!mole) return null;
        // Omitting `seconds` holds the mole up long enough for a test to whack
        // it deliberately. Passing `seconds: null` asks for the window the
        // round would really give it, which is how the GDD 6.6.2 visibility
        // ramp is asserted.
        const seconds = 'seconds' in args ? args.seconds : 5;
        this._pop(mole, args.type || 'standard', seconds);
        // Skip the rise animation so a test can whack it on the next frame.
        if (args.instant !== false) {
          mole.state = 'up';
          mole.timer = 0;
          this._setMoleHeight(mole, this._lerpHeight(mole, 1));
        }
        return { index: mole.index, type: mole.type, window: mole.window };
      }
      case 'whack': {
        const mole = this.moles[args.index ?? 0];
        if (!mole) return null;
        if (mole.state !== 'up' && mole.state !== 'rising') {
          return { index: mole.index, type: mole.type, points: 0, missed: true };
        }
        return this._hitMole(mole, args.side || 'right');
      }
      case 'time-scale':
        this._timeScale = Math.max(0, args.scale ?? 1);
        return this._timeScale;
      case 'set-time-left':
        this.timeLeft = Math.max(0, args.seconds ?? 0);
        this.updateScoreboard();
        return this.timeLeft;
      case 'state':
        return {
          elapsed: this._elapsed,
          timeLeft: this.timeLeft,
          progress: this._progress(),
          timeScale: this._timeScale,
          hits: { ...this.hits },
          misses: this.misses,
          mallets: this.mallets.length,
          moles: this.moles.map((m) => ({
            index: m.index,
            type: m.type,
            state: m.state,
            window: m.window,
            up: m.state === 'up' || m.state === 'rising',
          })),
        };
      default:
        return null;
    }
  }

  onTeardown() {
    for (const handle of this.mallets) grab.unregister(handle);
    this.mallets = [];
    this._malletRest = [];
    this.moles = [];
    this._timeScale = 1;
  }
}

registerStall({
  id: 'whack-a-mole',
  name: 'Whack-a-Mole',
  cost: CFG.cost,
  create: (ctx) => new WhackAMole(ctx),
});

export { WhackAMole };
